/**
 * Handler dos pedidos `get` a API.
 * Conforme a rota em que o utilizador se encontra é realizado
 * um pedido a API de forma a obter a informação dessa página.
 * Implementado validações de permissões e de pedidos.
 *
 * @since 1.0.0
 * @category Hook
 * @returns {Object}
 *
 */

// packages
import { reactive, toRefs, watch } from '@vue/composition-api';

// helpers
import { get } from '@/helpers/requests';

// utils
import { haveProperty } from '@/utils/';

// instance
import getRuntimeVM from '@/utils/runtime';

// store
import { mapState, useStore } from '@/hooks/use-store';

const response = mapState({ module: 'storePayload', key: 'response' });
const { getters } = useStore();

const useModels = () => {
    // obtém as routes;
    const vm = getRuntimeVM();
    let route = vm.$route;
    let isAddPage = false;
    let previousRouteIsAddPage = false;

    // definção do state
    const state = reactive({
        loading: false,
        error: false,
        data: {}
    });

    /**
     * Construção das permissões de cada
     * página.
     *
     * @since 1.0.0
     * @category Method
     * @autor Tiago Martinho
     *
    */
    const definePermissions = modelPermission => {
        return {
            permissions: {
                allowFilling: (
                    ( getters.can( modelPermission, 'add' ) && isAddPage )
                    || ( getters.can( modelPermission, 'edit' ) && !isAddPage )
                )
            }
        };
    };

    // reset do state
    const resetState = ({ newData = {}, newIsEdition, modelPermission }) => {
        state.data = {
            model: { ...newData },
            isEdition: newIsEdition,
            ...definePermissions( modelPermission )
        };
    };

    // Se falhar alguma guard, significa que
    // não pode fazer o pedido a API.
    const guards = {
        // Se existir uma resposta na store com a key `content`,
        // e se a anterior página é de adição ou se é necessário fazer update
        // ao modelo.
        // Como tal, é desnecessário fazero pedido a API, dado que esse novo
        // modelo esta presente na store.fpreviousRouteIsAddPage
        previousPageIsAdd: ( modelPermission, updateModel ) => {
            if ( !haveProperty( response.value, 'content' ) || !( previousRouteIsAddPage || updateModel ) )
                return false;

            resetState({ newData: response.value.content, newIsEdition: true, modelPermission });
            return true;
        },
        // Validação de permissão para entrar na página.
        // - Obtido o tipo de modelo de permissão.
        // - Conforme o tipo de página, é validado o tipo de permissão a ser validada.
        // - Obtém as validações da nova rota.
        // - Validado se o role do utilizador permite aceder a nova rota.
        //   - Caso não possa, é redirecionado para a página 404 enão executa o resto do código.
        permissionsValidation: modelPermission => {
            const typePermissions = !isAddPage ? 'see' : 'add';

            if ( getters.can( modelPermission, typePermissions ) )
                return false;

            vm.$router.push({ name: 'notFound', params: { 0: route.path } });
            return true;
        },
        // se for uma página para adicionar:
        // -- não executa o pedido a API.
        // -- reset do state
        // -- toggle da key `isEdition` para false
        // -- caso anteriormente seja uma página de edição
        //    é feito o reset dos campos anteriormente preenchidos.
        isAddPage: modelPermission => {
            if ( !isAddPage )
                return false;

            resetState({ newIsEdition: false, modelPermission });
            return true;
        },
        pagesWithoutRequest: () => {
            const pages = [ 'index', 'reporting', 'notFound' ];

            if ( !pages.includes( route.name ) )
                return false;

            return true;
        }
    };

    const fetch = async ({ updateModel = false }) => {
        const modelPermission = route.meta.parent || route.name;

        // validação de guards.
        if (
            guards.previousPageIsAdd( modelPermission, updateModel )
            || guards.permissionsValidation( modelPermission )
            || guards.isAddPage( modelPermission )
            || guards.pagesWithoutRequest()
        )
            return;

        const { specificEndpoint } = route.meta;

        const endpoint = specificEndpoint
            ? `${specificEndpoint}/${route.params.id || ''}`
            : route.path;

        const executeLoading = setTimeout( () => { state.loading = true; }, 400 );

        // validação do tipo de página
        const wasListPage = !haveProperty( route.meta, 'api' );

        const haveError = await get(
            endpoint,
            { ...( wasListPage && { paginate: 6 }), ...getQueryParams( route.query ) }
        )
            .then( ({ data }) => {
                clearTimeout( executeLoading );
                const key = wasListPage ? 'list' : 'model';
                state.data = {
                    [key]: data.content.data,
                    ...( wasListPage && { pagination: data.content.pagination }),
                    ...( !wasListPage && {
                        isEdition: !isAddPage,
                        ...definePermissions( modelPermission )
                    })
                };

                return false;
            })
            .catch( () => {
                clearTimeout( executeLoading );
                return false;
            });

        state.loading = haveError;
    };

    /**
     *
     * Função que prepara a query para ser feito o pedido à API.
     * Quando há parâmetros de pesquisa que são arrays mas quando
     * tem apenas uma opção do array selecionada, ao atualizar a página,
     * vai fazer o pedido como se fosse uma string, então tem de ser feito
     * um tratamento prévio da query para dar conta desta situação.
     *
     * @param {*} query
     * @return finalQuery
     *
     */
    const getQueryParams = query => {
        /* eslint-disable */
        const {
            name,
            campaignName,
            contactCallState,
            fromReceptionDate,
            toReceptionDate,
            page,
            ...otherParams
        } = query;
        /* eslint-enable */

        const finalQuery = {
            ...( name && { name }),
            ...( campaignName && { campaignName }),
            ...( contactCallState && { contactCallState }),
            ...( fromReceptionDate && { fromReceptionDate }),
            ...( toReceptionDate && { toReceptionDate }),
            ...( page && { page })
        };

        Object.keys( otherParams ).forEach( param => {
            finalQuery[param] = ( !Array.isArray( otherParams[param] ) )
                ? [ otherParams[param] ]
                : otherParams[param];
        });

        return finalQuery;
    };

    // listener para actualizar as rotas, conforme
    // o utilizador navega no software.
    watch( () => vm.$route,
        ( newRoute, previousRoute ) => {
            previousRouteIsAddPage = previousRoute?.meta.isAdd;
            state.data = {};
            route = newRoute;
            isAddPage = newRoute.meta.isAdd;

            fetch({});
        }, {
            immediate: true
        });

    return { ...toRefs( state ), fetch };
};

export default useModels;